/**
 * ShopPolicy — page /shop/spedizioni-resi
 * Legally-informed content for Italian e-commerce (D.Lgs. 206/2005 Codice del Consumo).
 * Sections: Spedizioni · Diritto di recesso 14gg · Garanzia legale 2 anni · Come chiedere il reso · Contatti.
 */
import { Link } from "react-router-dom";
import { ShopHeader, ShopFooter } from "@/pages/Shop";
import { Truck, RotateCcw, ShieldCheck, MailQuestion, ArrowLeft } from "lucide-react";

const Section = ({ id, icon: Icon, title, children }) => (
  <section id={id} className="border border-[#1a1a1a] bg-[#0a0a0a] p-6 md:p-8 mb-4" data-testid={`policy-${id}`}>
    <div className="flex items-center gap-3 mb-6">
      <Icon size={16} className="text-[#ff4b00]" />
      <h2 className="font-display text-2xl md:text-3xl">{title}</h2>
    </div>
    <div className="prose prose-invert max-w-none text-white/80 leading-relaxed space-y-4 [&_h3]:font-display [&_h3]:text-lg [&_h3]:text-white [&_h3]:mt-6 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_li]:mt-1 [&_strong]:text-white [&_a]:text-[#ff4b00] [&_a:hover]:underline">
      {children}
    </div>
  </section>
);

export default function ShopPolicy() {
  return (
    <div className="min-h-screen bg-black text-white">
      <ShopHeader />
      <main className="max-w-4xl mx-auto px-6 py-16" data-testid="shop-policy-page">
        <Link to="/shop" className="font-mono-tech text-[11px] text-white/50 hover:text-white flex items-center gap-2 mb-8"><ArrowLeft size={12} /> Torna al catalogo</Link>

        <div className="mb-12">
          <div className="font-mono-tech text-[10px] uppercase text-white/40 tracking-widest mb-3">◈ Condizioni di vendita</div>
          <h1 className="font-display text-5xl md:text-6xl">Spedizioni <em className="font-serif not-italic text-[#ff4b00]">&amp;</em> Resi</h1>
          <p className="mt-4 text-white/60 leading-relaxed max-w-2xl">
            Documento redatto ai sensi del D.Lgs. 206/2005 (Codice del Consumo) e successive modifiche.
            Ultimo aggiornamento: {new Date().toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}.
          </p>
          <nav className="mt-6 flex flex-wrap gap-2 font-mono-tech text-[11px]">
            <a href="#spedizioni" className="border border-[#1a1a1a] px-3 py-1.5 hover:border-[#ff4b00] hover:text-[#ff4b00]">Spedizioni</a>
            <a href="#recesso" className="border border-[#1a1a1a] px-3 py-1.5 hover:border-[#ff4b00] hover:text-[#ff4b00]">Diritto di recesso</a>
            <a href="#garanzia" className="border border-[#1a1a1a] px-3 py-1.5 hover:border-[#ff4b00] hover:text-[#ff4b00]">Garanzia legale</a>
            <a href="#procedura" className="border border-[#1a1a1a] px-3 py-1.5 hover:border-[#ff4b00] hover:text-[#ff4b00]">Procedura reso</a>
            <a href="#contatti" className="border border-[#1a1a1a] px-3 py-1.5 hover:border-[#ff4b00] hover:text-[#ff4b00]">Contatti</a>
          </nav>
        </div>

        <Section id="spedizioni" icon={Truck} title="Spedizioni">
          <h3>Tempi di preparazione</h3>
          <p>Gli oggetti proposti nello shop sono realizzati artigianalmente in piccola serie nella nostra officina di Budoia (PN).
            Salvo diversa indicazione sulla scheda prodotto, gli ordini vengono preparati e spediti entro <strong>3-5 giorni lavorativi</strong> dalla ricezione del pagamento.</p>

          <h3>Corriere e tempi di consegna</h3>
          <p>Le spedizioni vengono affidate a corriere espresso nazionale. I tempi di consegna medi in Italia sono di <strong>24-72 ore lavorative</strong> dalla presa in carico, con possibili variazioni per zone remote o isole.
            Una volta spedito l&apos;ordine riceverai automaticamente via email il <strong>codice di tracciamento</strong>.</p>

          <h3>Costi di spedizione</h3>
          <p>Il costo è calcolato in base al peso complessivo dell&apos;ordine e viene visualizzato in fase di checkout, prima della conferma del pagamento. Le fasce indicative sono:</p>
          <ul>
            <li>Fino a 500 g · € 6,00</li>
            <li>Da 500 g a 1 kg · € 7,00</li>
            <li>Da 1 kg a 2 kg · € 9,00</li>
            <li>Da 2 kg a 5 kg · € 12,00</li>
            <li>Da 5 kg a 10 kg · € 18,00</li>
            <li>Oltre 10 kg · calcolato dinamicamente in checkout</li>
          </ul>
          <p className="text-white/60 text-sm">Le fasce sono aggiornabili dall&apos;amministratore e i valori esatti applicati al tuo ordine sono sempre quelli mostrati in checkout.</p>

          <h3>Ritardi, mancata consegna, danneggiamenti</h3>
          <p>In caso di ritardi imputabili al corriere non risponderemo del disservizio, ma ti supporteremo per aprire un reclamo.
            Se il pacco arriva <strong>visibilmente danneggiato</strong>, ti invitiamo a rifiutare la consegna o ad accettarla con <em>riserva scritta</em> sul documento di trasporto e a segnalarcelo entro 48 ore.</p>

          <h3>Ritiro presso la nostra officina</h3>
          <p>Se preferisci ritirare in sede a Budoia (PN) senza costi di spedizione, contattaci prima dell&apos;ordine e ti forniremo istruzioni.</p>
        </Section>

        <Section id="recesso" icon={RotateCcw} title="Diritto di recesso (14 giorni)">
          <p>Ai sensi degli artt. 52 e seguenti del <strong>Codice del Consumo</strong>, se sei un consumatore hai diritto di recedere dall&apos;acquisto <strong>entro 14 giorni dalla consegna</strong>, senza dover fornire alcuna motivazione.</p>

          <h3>Cosa serve per esercitarlo</h3>
          <ul>
            <li>Inviarci una comunicazione entro 14 giorni all&apos;indirizzo <a href="mailto:shop@gefion-evolution.it">shop@gefion-evolution.it</a> indicando: numero ordine, prodotto/i restituito/i, IBAN per il rimborso.</li>
            <li>Rispedire il prodotto <strong>integro, non usato, nella confezione originale</strong> entro ulteriori 14 giorni dalla comunicazione.</li>
          </ul>

          <h3>Chi paga il rientro?</h3>
          <p>Le <strong>spese di rispedizione sono a carico del cliente</strong>. Ti consigliamo di utilizzare un corriere con tracking e assicurazione: fino alla nostra ricezione il rischio di smarrimento o danneggiamento resta a tuo carico.</p>

          <h3>Rimborso</h3>
          <p>Verificata l&apos;integrità del prodotto, provvederemo al rimborso di quanto pagato per l&apos;articolo (esclusa la spesa di spedizione iniziale se il rientro è parziale) <strong>entro 14 giorni</strong> dalla ricezione, con lo stesso metodo di pagamento utilizzato per l&apos;ordine oppure tramite bonifico bancario.</p>

          <h3>Eccezioni al diritto di recesso</h3>
          <p>Ai sensi dell&apos;art. 59 del Codice del Consumo, il diritto di recesso <strong>non si applica</strong> nei seguenti casi:</p>
          <ul>
            <li>Prodotti realizzati <strong>su misura o personalizzati</strong> sulla base di richieste specifiche del cliente (es. incisioni, misure custom, colori dedicati).</li>
            <li>Prodotti che, per loro natura, non possono essere restituiti (es. articoli sigillati aperti dopo la consegna).</li>
          </ul>
          <p className="text-white/60 text-sm">La maggior parte degli oggetti nella nostra collezione &egrave; realizzata in piccola serie non personalizzata: il diritto di recesso si applica normalmente. Le eventuali eccezioni sono chiaramente indicate nella scheda del prodotto.</p>
        </Section>

        <Section id="garanzia" icon={ShieldCheck} title="Garanzia legale di conformità (2 anni)">
          <p>Tutti i prodotti sono coperti dalla <strong>garanzia legale di conformità di 24 mesi</strong> prevista dagli artt. 128 e seguenti del Codice del Consumo.</p>
          <p>Se il prodotto presenta un difetto di conformità (funzionamento, materiale, finitura non conforme a quanto descritto), hai diritto — a nostra scelta e senza costi — a:</p>
          <ul>
            <li><strong>Riparazione</strong> del prodotto, oppure</li>
            <li><strong>Sostituzione</strong> con uno equivalente, oppure</li>
            <li>Se la riparazione o sostituzione non sono possibili, alla <strong>riduzione del prezzo</strong> o alla <strong>risoluzione del contratto</strong> con rimborso.</li>
          </ul>
          <p>Il difetto deve essere segnalato entro <strong>2 mesi dalla scoperta</strong>. Trattandosi di oggetti artigianali, piccole variazioni di finitura, spessore o colore fra pezzi della stessa serie sono considerate caratteristica del prodotto e non difetto.</p>
        </Section>

        <Section id="procedura" icon={MailQuestion} title="Come chiedere un reso · procedura pratica">
          <ol className="list-decimal pl-6 space-y-3">
            <li><strong>Scrivici</strong> a <a href="mailto:shop@gefion-evolution.it">shop@gefion-evolution.it</a> indicando numero ordine e motivo del reso (recesso o difetto).</li>
            <li>Ti risponderemo entro <strong>2 giorni lavorativi</strong> con l&apos;indirizzo di rientro e le istruzioni.</li>
            <li>Imballa con cura il prodotto — meglio se nella confezione originale — e spediscilo con corriere tracciato.</li>
            <li>Alla ricezione controlleremo l&apos;integrit&agrave; e ti confermeremo il rimborso via email.</li>
            <li>Il rimborso arriver&agrave; sul tuo metodo di pagamento entro <strong>14 giorni</strong> dalla verifica.</li>
          </ol>
        </Section>

        <Section id="contatti" icon={MailQuestion} title="Contatti e reclami">
          <p>Per qualsiasi domanda o reclamo relativo a ordini, spedizioni, resi e garanzie puoi contattarci ai seguenti recapiti:</p>
          <ul>
            <li><strong>Email dedicata shop</strong>: <a href="mailto:shop@gefion-evolution.it">shop@gefion-evolution.it</a></li>
            <li><strong>PEC</strong>: <a href="mailto:gefionsrl@legalmail.it">gefionsrl@legalmail.it</a></li>
            <li><strong>Indirizzo</strong>: Gefion S.r.l. · Via 1° Maggio 6 · 33070 Budoia (PN) · Italia</li>
            <li><strong>P.IVA / C.F.</strong>: 01675990939</li>
          </ul>
          <p>In caso di controversie il consumatore può accedere alla piattaforma europea ODR (<a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer">ec.europa.eu/consumers/odr</a>) o rivolgersi al foro del proprio luogo di residenza.</p>
        </Section>

        <p className="mt-8 font-mono-tech text-[10px] text-white/40">
          Questo documento pu&ograve; essere modificato in qualsiasi momento con aggiornamento della data in cima alla pagina. Le condizioni valide per ciascun ordine sono quelle in vigore al momento della conferma.
        </p>
      </main>
      <ShopFooter />
    </div>
  );
}
